import React from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { images } from '../data/images';
import { store } from '../data/store';
import { ApplicationForm } from '../components/concept/ApplicationForm';
import { PermanenceWeek } from '../components/home/PermanenceWeek';
import { easeOut, staggerDelay } from '../utils/motion';
const steps = [{
  title: 'Une sélection',
  text: 'Le collectif choisit des créateurs dont le travail est fait main, en petites séries, de préférence près d’ici.'
}, {
  title: 'Trois mois en vitrine',
  text: 'Chaque créateur dispose d’environ trois mois d’exposition. Ses pièces sont en boutique et en ligne pendant cette période.'
}, {
  title: 'Des permanences partagées',
  text: 'Les créateurs tiennent la boutique à tour de rôle. Ils vendent leur travail, et celui des autres, avec la même attention.'
}, {
  title: 'Place au suivant',
  text: 'À la fin de la période, un autre créateur s’installe. La boutique se renouvelle sans jamais fermer.'
}];
export function Concept() {
  return <>
      <section className="mx-auto max-w-[1440px] px-5 pt-10 md:px-8 md:pt-16" data-id="element-714" data-mp="3fl0piprl10rf">
        <h1 className="max-w-5xl font-display text-5xl leading-[0.98] md:text-8xl" data-id="element-715" data-mp="3kev6hlfsz1mt">
          Une boutique qui ne tient <em className="text-clay" data-id="element-716" data-mp="3pc3rdtsc5jrr">jamais en place.</em>
        </h1>
        <div className="mt-8 grid gap-6 md:grid-cols-12" data-id="element-717" data-mp="2rpo9wrzwsm47">
          <p className="text-lg leading-relaxed text-muted md:col-span-6 md:col-start-7" data-id="element-718" data-mp="2rr94uiq4y72l">
            L’Éphémère Perpétuel accueille une vingtaine de créateurs qui exposent leurs productions : bijoux, mode, céramique,
            bois, luminaires, savons, miel… Comme le souligne le Petit Futé, chacun dispose d’une période d’exposition
            d’environ trois mois, et tous assurent à tour de rôle des permanences.
          </p>
        </div>
        <div className="mt-12 aspect-[16/9] overflow-hidden rounded-sm bg-shell md:aspect-[21/9]" data-id="element-719" data-mp="luvb3ed9arz5">
          <img src={images.hero} alt="La boutique, ses étagères en bois et les créations exposées" className="h-full w-full object-cover" data-id="element-720" data-mp="2hy00ypkpx072" />
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 py-20 md:px-8 md:py-28" aria-labelledby="fonctionnement" data-id="element-721" data-mp="2qzheq1ankpsl">
        <h2 id="fonctionnement" className="font-display text-4xl md:text-5xl" data-id="element-722" data-mp="2mxmgj5o0qsj1">
          Comment ça tourne
        </h2>
        <ol className="relative mt-12 grid gap-10 md:grid-cols-4 md:gap-8" data-id="element-723" data-mp="1z9mlhtjocfyn">
          <span className="absolute left-0 right-0 top-[18px] hidden h-px bg-line md:block" aria-hidden data-id="element-724" data-mp="1ua091mbo73qw" />
          {steps.map((s, i) => <motion.li key={s.title} className="relative" initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-60px'
        }} transition={{
          duration: 0.28,
          ease: easeOut,
          delay: staggerDelay(i, 0.06)
        }} data-id="element-725" data-mp="282g9qgh3mpon" data-mpi={i}>
              <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-ink font-display text-lg text-paper" data-id="element-726" data-mp="11fbymz8k44ii">
                {i + 1}
              </span>
              <h3 className="mt-5 font-display text-2xl" data-id="element-727" data-mp="167llg6edkogk">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-muted" data-id="element-728" data-mp="rifnsmv6r6as">{s.text}</p>
            </motion.li>)}
        </ol>
      </section>

      <section id="visite" className="scroll-mt-28 bg-shell" aria-labelledby="visite-title" data-id="element-729" data-mp="1k5tbduczupi8">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-20 md:px-8 lg:grid-cols-12" data-id="element-730" data-mp="1p4mohlodcehv">
          <div className="lg:col-span-5" data-id="element-731" data-mp="1a7duek7okwmg">
            <h2 id="visite-title" className="font-display text-4xl md:text-5xl" data-id="element-732" data-mp="1a6carrcln1t4">
              Nous rendre <em className="text-clay" data-id="element-733" data-mp="1f5fen8guf1zi">visite</em>
            </h2>
            <ul className="mt-8 space-y-5 text-sm" data-id="element-734" data-mp="3uxutjbwj6tq4">
              <li className="flex gap-3" data-id="element-735" data-mp="3uw1n9xptybin">
                <MapPinIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden data-id="element-736" data-mp="3ra4fxq1dvvq" />
                <span data-id="element-737" data-mp="3p7ax3podkm9">
                  {store.address}
                  <br data-id="element-738" data-mp="jzhpm83il33c" />
                  {store.city}
                </span>
              </li>
              <li className="flex gap-3" data-id="element-739" data-mp="f6on7qviq6uf">
                <ClockIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden data-id="element-740" data-mp="f5n0gp553vwv" />
                <span data-id="element-741" data-mp="a94w3qdq1zl2">
                  {store.hours.map(h => <span key={h.days} className="block" data-id="element-742" data-mp="5ablc5ptkmol">
                      {h.days} : {h.time}
                    </span>)}
                </span>
              </li>
              <li className="flex gap-3" data-id="element-743" data-mp="xxo7wfvzcuhw">
                <PhoneIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden data-id="element-744" data-mp="xyqz6ox7syyc" />
                <a href={`tel:${store.phone.replace(/\s/g, '')}`} className="hover:text-clay" data-id="element-745" data-mp="sy39c6kq2o5p">
                  {store.phone}
                </a>
              </li>
              <li className="flex gap-3" data-id="element-746" data-mp="3dp3ouajkr1la">
                <MailIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden data-id="element-747" data-mp="3dq4z4csrnk1y" />
                <a href={`mailto:${store.email}`} className="hover:text-clay" data-id="element-748" data-mp="38pzevhotsztw">
                  {store.email}
                </a>
              </li>
            </ul>
            <p className="mt-8 max-w-sm text-sm text-muted" data-id="element-749" data-mp="1o6dkj47k2zr2">
              Les commandes en « retrait en boutique » sont prêtes sous 24 h et vous sont remises par le créateur de
              permanence.
            </p>
          </div>
          <div className="lg:col-span-6 lg:col-start-7" data-id="element-750" data-mp="22xdzzt6pztwp">
            <h3 className="mb-4 text-sm font-medium" data-id="element-751" data-mp="22yxjrtrl4n38">Les permanences de la semaine</h3>
            <PermanenceWeek data-id="element-752" data-mp="27ya4o8ywbk36" />
          </div>
        </div>
      </section>

      <section id="candidature" className="mx-auto max-w-[1440px] scroll-mt-28 px-5 py-20 md:px-8 md:py-28" aria-labelledby="candidature-title" data-id="element-753" data-mp="2bwhj2x5w09b7">
        <div className="grid gap-12 lg:grid-cols-12" data-id="element-754" data-mp="2lvynlb9ekh5l">
          <div className="lg:col-span-5" data-id="element-755" data-mp="2lvgjxtkev0nn">
            <h2 id="candidature-title" className="font-display text-4xl leading-[1.02] md:text-5xl" data-id="element-756" data-mp="miumfdp9c218">
              Vous créez ? <em className="text-clay" data-id="element-757" data-mp="rhfm9c0lkjp5">Exposez chez nous.</em>
            </h2>
            <p className="mt-5 text-muted" data-id="element-758" data-mp="jh9mqqxbcuaz">
              Une période de trois mois en vitrine et en ligne, en échange de quelques permanences par mois. Les saisons se
              composent environ six mois à l’avance.
            </p>
          </div>
          <div className="lg:col-span-6 lg:col-start-7" data-id="element-759" data-mp="tg8mtl40jhmd">
            <ApplicationForm data-id="element-760" data-mp="ogm97ak9vzd1" />
          </div>
        </div>
      </section>
    </>;
}